import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { FormikProps } from 'formik';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import Platform from '@lyearn/core/utils/Platform';
import { useTranslation } from '@lyearn/i18n';
import { Box, Button, LinearProgress, Skeleton, Stack, Typography } from '@lyearn/ui';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import WideCardLoader from '@/modules/content/components/ContentCard/Wide/Loader';
import WideCard from '@/modules/content/components/ContentCard/WideCard';
import { ContentCardTypeEnum } from '@/modules/content/config/cards';
import SurveyStatus from '@/modules/content/modules/survey/components/SurveyStatus';
import { SurveyContentCardFragment } from '@/modules/content/modules/survey/fragments/generated/SurveyContentCard';
import { cardImagePropsConfig } from '@/modules/content/utils/renderContentCard';
import DatabaseForm from '@/modules/corporate/modules/database/views/FormView/components/DatabaseForm';
import parseSurveyFormValues from '@/modules/corporate/modules/survey/views/SurveyDetails/components/SurveyContent/utils/parseSurveyFormValues';
import { useUpdateSurveyPerformanceMutation } from '@/modules/corporate/modules/survey/views/SurveyDetails/graphql/mutations/generated/updateSurveyPerformance';
import useSurveyDetails from '@/modules/corporate/modules/survey/views/SurveyDetails/hooks/useSurveyDetails';
import { PerformanceStatusEnum } from '@/types/schema';

import { surveyWideCardStyles } from '../CourseSurveyCard/styles';
import { CourseSurveyCardProps } from '../CourseSurveyCard/types';

const mobileImageProps = {
  height: 98,
  width: 80,
};

// Shown briefly while react-i18next loads the field namespaces on first expand.
function FieldSkeleton() {
  return (
    <Stack className="py-16 px-20" direction="col" gapY={12}>
      <Skeleton animation="wave" height={20} variant="rectangular" width="40%" />
      <Skeleton
        animation="wave"
        className="rounded-8"
        height={48}
        variant="rectangular"
        width="100%"
      />
    </Stack>
  );
}

function InlineSurveyContainer(props: CourseSurveyCardProps) {
  const { courseId, surveyId, onLoad, onSubmit } = props;
  const { isMobile } = useScreen();
  const isNative = Platform.OS !== 'web';
  const { t } = useTranslation();
  const user = useUserContext();

  const [isExpanded, { on: expand, off: collapse }] = useToggle(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formikHandleSubmitRef = useRef<(() => void) | undefined>();
  const setFormikSubmit = useCallback((getter: () => () => void) => {
    formikHandleSubmitRef.current = getter();
  }, []);
  const currentFieldValuesRef = useRef<Record<string, any>>({});

  const [{ fetching: submitting }, updateSurveyPerformance] = useUpdateSurveyPerformanceMutation();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();

  const { fetching, error, survey, fields } = useSurveyDetails({
    contentId: surveyId,
    isEmbed: true,
    parentContentId: courseId,
  });

  useEffect(() => {
    if (survey) {
      onLoad(survey.duration);
    }
  }, [onLoad, survey]);

  const isSurveyComplete =
    survey?.performance?.status === PerformanceStatusEnum.Completed || isSubmitted;

  const handleCollapse = useCallback(() => {
    collapse();
    setCurrentIndex(0);
    setAnswers({});
    setIsSubmitted(false);
    currentFieldValuesRef.current = {};
  }, [collapse]);

  const submitSurvey = useCallback(
    async (finalAnswers: Record<string, any>) => {
      const { fieldsData } = parseSurveyFormValues({
        parentContentId: courseId,
        values: finalAnswers,
        fields,
      });
      const response = await updateSurveyPerformance({
        input: {
          userId: user.id,
          surveyId,
          status: PerformanceStatusEnum.Completed,
          databaseDataInput: { dbId: 'survey', fields: fieldsData },
          isEmbed: true,
        },
      });
      if (response.data?.updateSurveyPerformance.success) {
        setIsSubmitted(true);
        collapse();
        onSubmit();
      } else {
        showDefaultErrorSnackbar();
      }
    },
    [
      collapse,
      courseId,
      fields,
      onSubmit,
      showDefaultErrorSnackbar,
      surveyId,
      updateSurveyPerformance,
      user.id,
    ],
  );

  const onStepSubmit = useCallback(
    (values: any) => {
      currentFieldValuesRef.current = {};
      const newAnswers = { ...answers, ...values };
      setAnswers(newAnswers);
      if (currentIndex < fields.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        submitSurvey(newAnswers);
      }
    },
    [answers, currentIndex, fields.length, submitSurvey],
  );

  const onBack = useCallback(() => {
    if (currentIndex === 0) {
      handleCollapse();
    } else {
      setAnswers((prev) => ({ ...prev, ...currentFieldValuesRef.current }));
      currentFieldValuesRef.current = {};
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex, handleCollapse]);

  // Only show the loader on the initial fetch (no cached data yet).
  if (!survey) {
    return fetching ? <WideCardLoader className="py-60" /> : null;
  }

  if (error) {
    return null;
  }

  // A LOCAL Suspense boundary is required around the expanded player. The app
  // runs react-i18next in suspense mode, so the first render of DatabaseForm,
  // which pulls the unloaded 'Database' namespace, suspends. Without a boundary
  // here, the suspension bubbles to an ancestor boundary in the course viewer,
  // whose fallback unmounts this whole component — destroying `isExpanded` and
  // collapsing back to the card. That is the "flickers twice, only opens on the
  // third click" bug. The boundary lives BELOW the state-holding component so
  // suspension is caught locally and `isExpanded` survives.
  if (isExpanded && !isSurveyComplete) {
    const currentField = fields[currentIndex];
    if (!currentField) return null;
    const isLastField = currentIndex === fields.length - 1;

    return (
      <Box className="border border-border rounded-16 overflow-hidden">
        <Stack
          alignItems="center"
          className="relative px-20 pt-20 pb-4"
          direction="row"
          justifyContent="between">
          <LinearProgress
            className="absolute inset-x-0 top-0"
            size="x-small"
            value={((currentIndex + 1) / fields.length) * 100}
          />
          <Typography color="text-text-secondary" variant="heading-xxs">
            {currentIndex + 1} / {fields.length}
          </Typography>
        </Stack>
        <Suspense fallback={<FieldSkeleton />}>
          <DatabaseForm
            key={currentIndex}
            className="py-16 px-20"
            fields={[currentField]}
            initialValues={{
              [currentField.metadataField.fieldId]: answers[currentField.metadataField.fieldId],
            }}
            setFormikSubmit={setFormikSubmit}
            onChange={(formik: FormikProps<Record<string, any>>) => {
              currentFieldValuesRef.current = formik.values;
            }}
            onSubmit={onStepSubmit}
          />
        </Suspense>
        <Stack className="px-20 pb-20" direction="row" gapX={8}>
          <Button variant="outlined" onClick={onBack}>
            {currentIndex === 0 ? t('Common:Actions.Cancel') : t('Common:Actions.Back')}
          </Button>
          <Button
            loading={isLastField && submitting}
            onClick={() => formikHandleSubmitRef.current?.()}>
            {isLastField ? t('Common:Actions.Submit') : t('Common:Actions.Next')}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className={isSurveyComplete ? undefined : 'cursor-pointer'}>
      <WideCard
        isEmbed
        className={isNative || isMobile ? undefined : surveyWideCardStyles}
        content={survey as unknown as SurveyContentCardFragment}
        imageProps={isMobile ? mobileImageProps : cardImagePropsConfig[ContentCardTypeEnum.Wide]}
        renderInfo={() => (isSurveyComplete ? <SurveyStatus /> : <></>)}
        onClick={isSurveyComplete ? undefined : expand}
      />
    </Box>
  );
}

export default React.memo(InlineSurveyContainer);
